import { useState } from "react";
import { Eye, EyeOff, KeyRound, LogOut } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { messageServeur } from "@/lib/erreur-serveur";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Premier passage avec le mot de passe provisoire de la fiche de connexion
 * (27/10/2026) : la personne choisit son mot de passe avant toute autre page.
 * auth-service révoque les sessions au changement ; on se reconnecte aussitôt
 * avec le nouveau mot de passe, sans rien redemander.
 */
export default function ChoisirMotDePasse() {
  const { user, loginMutation, logoutMutation } = useAuth();
  const [provisoire, setProvisoire] = useState("");
  const [nouveau, setNouveau] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [voir, setVoir] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const prenom = user?.fullName?.split(" ")[0] ?? "";

  async function valider(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    if (nouveau.length < 8) {
      setErreur("Votre nouveau mot de passe doit avoir au moins 8 caractères.");
      return;
    }
    if (nouveau !== confirmation) {
      setErreur("Les deux saisies du nouveau mot de passe ne sont pas identiques.");
      return;
    }
    if (nouveau === provisoire) {
      setErreur("Choisissez un mot de passe différent de celui de la fiche.");
      return;
    }
    setEnvoi(true);
    try {
      await apiRequest("POST", "/api/auth/change-password", {
        currentPassword: provisoire,
        newPassword: nouveau,
      });
      await loginMutation.mutateAsync({ username: user!.username, password: nouveau });
    } catch (err) {
      setErreur(
        messageServeur(
          err,
          { 401: "Le mot de passe de la fiche n'est pas le bon. Recopiez-le lettre par lettre." },
          "Le changement n'a pas pu se faire. Réessayez dans un instant.",
        ),
      );
    } finally {
      setEnvoi(false);
    }
  }

  const type = voir ? "text" : "password";

  return (
    <div
      className="flex min-h-screen w-full items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #1A2B4A 0%, #2a4a7f 40%, #3b7dd8 100%)" }}
    >
      <form
        onSubmit={valider}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl sm:p-8 dark:bg-gray-900"
        data-testid="form-choisir-mot-de-passe"
      >
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--brand-navy,#1A2B4A)] text-white">
            <KeyRound className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-50">
              Choisissez votre mot de passe
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {prenom ? `Bienvenue, ${prenom}. ` : ""}C'est la seule fois qu'on vous le demande.
            </p>
          </div>
        </div>

        <p className="mb-5 rounded-lg bg-blue-50 p-3 text-sm leading-relaxed text-gray-800 dark:bg-blue-950 dark:text-gray-100">
          Le mot de passe de votre fiche est provisoire. Remplacez-le par un mot de passe que vous
          seul connaissez, d'au moins 8 caractères. Il servira aussi pour l'Académie CATHO.
        </p>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="mdp-provisoire">Mot de passe de la fiche</Label>
            <Input
              id="mdp-provisoire"
              type={type}
              autoComplete="current-password"
              value={provisoire}
              onChange={(e) => setProvisoire(e.target.value)}
              required
              data-testid="input-mdp-provisoire"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mdp-nouveau">Nouveau mot de passe</Label>
            <Input
              id="mdp-nouveau"
              type={type}
              autoComplete="new-password"
              value={nouveau}
              onChange={(e) => setNouveau(e.target.value)}
              required
              data-testid="input-mdp-nouveau"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mdp-confirmation">Retapez le nouveau mot de passe</Label>
            <Input
              id="mdp-confirmation"
              type={type}
              autoComplete="new-password"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              required
              data-testid="input-mdp-confirmation"
            />
          </div>
          <button
            type="button"
            onClick={() => setVoir((v) => !v)}
            className="flex items-center gap-2 text-sm text-gray-700 underline-offset-2 hover:underline dark:text-gray-200"
          >
            {voir ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {voir ? "Masquer les mots de passe" : "Afficher les mots de passe"}
          </button>
        </div>

        {erreur && (
          <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-100">
            {erreur}
          </p>
        )}

        <Button type="submit" className="mt-6 h-11 w-full text-base" disabled={envoi} data-testid="btn-enregistrer-mdp">
          {envoi ? "Enregistrement…" : "Enregistrer mon mot de passe"}
        </Button>
        <button
          type="button"
          onClick={() => logoutMutation.mutate()}
          className="mx-auto mt-4 flex items-center gap-2 text-sm text-gray-600 hover:underline dark:text-gray-300"
        >
          <LogOut className="h-4 w-4" /> Me déconnecter
        </button>
      </form>
    </div>
  );
}
